import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SearchX, ChevronLeft, ChevronRight, Users, Filter } from "lucide-react";
import axiosInstance from "../../../api/axiosInstance";
import ExpertCard from "./ExpertCard";
import FilterSection from "./FilterSection";

// --- Sub-Component: Premium Pagination ---
const Pagination = ({ expertsPerPage, totalExperts, paginate, currentPage }) => {
  const totalPages = Math.ceil(totalExperts / expertsPerPage);
  if (totalPages <= 1) return null;

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
      pageNumbers.push(i);
    }
  }

  return (
    <div className="flex flex-col items-center gap-6 py-20">
      <div className="flex items-center gap-2 p-1.5 bg-white/80 backdrop-blur-md rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-200/50">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className="p-3 rounded-xl hover:bg-slate-50 text-slate-500 disabled:opacity-20 transition-all cursor-pointer group"
        >
          <ChevronLeft size={18} className="group-hover:-translate-x-0.5 transition-transform" />
        </button>

        <div className="flex gap-1">
          {pageNumbers.map((p, i) => (
            <React.Fragment key={p}>
              {i > 0 && pageNumbers[i - 1] !== p - 1 && (
                <span className="text-slate-300 self-center px-2">•••</span>
              )}
              <button
                onClick={() => paginate(p)}
                className={`w-12 h-12 rounded-xl font-bold text-sm transition-all duration-500 ${
                  currentPage === p
                    ? "bg-blue-600 text-white shadow-xl shadow-blue-200 ring-4 ring-blue-50"
                    : "text-slate-500 hover:bg-blue-50 hover:text-blue-600"
                }`}
              >
                {p}
              </button>
            </React.Fragment>
          ))}
        </div>

        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="p-3 rounded-xl hover:bg-slate-50 text-slate-500 disabled:opacity-20 transition-all cursor-pointer group"
        >
          <ChevronRight size={18} className="group-hover:translate-x-0.5 transition-transform" />
        </button>
      </div>
    </div>
  );
};

// --- Main Component: ExpertList ---
const ExpertList = () => {
  const [allExperts, setAllExperts] = useState([]);
  const [filteredExperts, setFilteredExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const expertsPerPage = 8;

  useEffect(() => {
    fetchExperts();
  }, []);

  const fetchExperts = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/users/vendors");
      if (res.data.success) {
        setAllExperts(res.data.data);
        setFilteredExperts(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching experts:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFilterResults = useCallback((filtered) => {
    setFilteredExperts(filtered);
    setCurrentPage(1);
  }, []);

  const currentExperts = filteredExperts.slice(
    (currentPage - 1) * expertsPerPage,
    currentPage * expertsPerPage
  );
  
  const onlineCount = allExperts.filter(e => e.isOnline).length;

  return (
    <div className="min-h-[400px] font-outfit">
      {/* Filter Bar */}
      <FilterSection onFilter={handleFilterResults} />

      <div className="max-w-7xl mx-auto px-6 pt-8">
        {/* Compact Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4 pb-6 border-b border-slate-100">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800">Our Experts</h2>
              <p className="text-xs text-slate-500 mt-0.5">Connect with verified professionals vetted for quality and reliability.</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-50 rounded-xl border border-slate-200">
              <div className="relative">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping absolute inset-0" />
                <div className="w-2 h-2 bg-emerald-500 rounded-full relative" />
              </div>
              <span className="text-xs font-bold text-slate-700">{onlineCount} Available Now</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-900 rounded-xl text-white">
              <Users size={14} className="text-blue-400" />
              <span className="text-xs font-bold">{filteredExperts.length} Experts</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-[420px] bg-white rounded-[2.5rem] border border-slate-100 animate-pulse flex flex-col p-6">
                <div className="w-full h-48 bg-slate-50 rounded-3xl mb-4" />
                <div className="h-6 w-3/4 bg-slate-50 rounded-lg mb-2" />
                <div className="h-4 w-1/2 bg-slate-50 rounded-lg" />
              </div>
            ))}
          </div>
        ) : filteredExperts.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-16 bg-white rounded-3xl"
          >
            <div className="relative mb-6">
              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 border border-slate-100">
                <SearchX size={28} />
              </div>
            </div>
            <h3 className="text-base font-bold text-slate-800 tracking-tight uppercase">No Matching Experts</h3>
            <p className="text-xs text-slate-500 font-medium mt-1.5 mb-6 max-w-xs text-center px-4">
              We couldn't find any experts matching your current criteria. Try widening your search or clearing filters.
            </p>
            <button 
              onClick={fetchExperts}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-xs rounded-xl shadow-md shadow-blue-500/10 transition-all active:scale-[0.98]"
            >
              <Filter size={12} /> Clear Preferences
            </button>
          </motion.div>
        ) : (
          <div className="space-y-12">
            <AnimatePresence mode="popLayout">
              <motion.div 
                layout
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10"
              >
                {currentExperts.map((ex, idx) => (
                  <motion.div
                    key={ex.id || ex._id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4, delay: (idx % 4) * 0.1 }}
                  >
                    <ExpertCard 
                      {...ex} 
                      id={ex.id || ex._id}
                      name={ex.name || `${ex.firstName} ${ex.lastName}`}
                      rating={ex.averageRating}
                      specialization={ex.vendorType}
                      experience={ex.experienceYears}
                    />
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>

            <Pagination
              expertsPerPage={expertsPerPage}
              totalExperts={filteredExperts.length}
              paginate={handlePageChange}
              currentPage={currentPage}
            />
          </div>
        )}
      </div>

    </div>
  );
};

export default ExpertList;